import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Empty, Modal, Spin, Tag, Tooltip } from "antd";
import { CheckOutlined, ClearOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";
import { GreenNotify, RedNotify } from "../../helper/helper";
import {
  deleteAllNotifications,
  deleteNotification,
  markAllNotificationsSeen,
  markNotificationsRead,
  notificationDeepLink,
} from "../../helper/notifications";
import "./notifications.css";

dayjs.extend(relativeTime);

// A Firestore Timestamp exposes .toDate(); guard against serverTimestamp()
// placeholders (null while pending) and plain ISO strings just in case.
const toJsDate = (sentAt) => {
  if (!sentAt) return null;
  if (typeof sentAt.toDate === "function") return sentAt.toDate();
  if (sentAt.seconds != null) return new Date(sentAt.seconds * 1000);
  const parsed = dayjs(sentAt);
  return parsed.isValid() ? parsed.toDate() : null;
};

const formatTimestamp = (sentAt) => {
  const date = toJsDate(sentAt);
  if (!date) return "—";
  return dayjs(date).format("MMM D, YYYY · h:mm A");
};

const relativeLabel = (sentAt) => {
  const date = toJsDate(sentAt);
  if (!date) return "";
  return dayjs(date).fromNow();
};

const Notifications = () => {
  const user = useSelector((state) => state.userDataSlice?.userData);
  const adminUid = user?.uuid || user?._id || null;
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  // Opening the inbox marks ALL unseen notifications seen (query-driven, not
  // just the paged 50 below), so the bell badge fully clears even for a large
  // backlog. Rows stay bold until individually read.
  useEffect(() => {
    if (adminUid) markAllNotificationsSeen(adminUid).catch(() => {});
  }, [adminUid]);

  // Realtime list — stays current while open.
  useEffect(() => {
    if (!adminUid) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    const q = query(
      collection(db, "notifications"),
      where("to", "==", adminUid),
      orderBy("sentAt", "desc"),
      limit(50)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (error) => {
        console.warn("Failed to load notifications:", error?.message);
        setLoading(false);
        RedNotify("Could not load notifications. Please try again.");
      }
    );
    return unsub;
  }, [adminUid]);

  const unreadCount = useMemo(
    () => items.filter((item) => !item.isRead).length,
    [items]
  );

  const markAsRead = useCallback(async (id) => {
    setBusyId(id);
    try {
      await updateDoc(doc(db, "notifications", id), {
        isRead: true,
        seen: true,
      });
    } catch (error) {
      console.warn("Failed to mark notification read:", error?.message);
      RedNotify("Could not update notification.");
    } finally {
      setBusyId(null);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const unread = items.filter((i) => !i.isRead).map((i) => i.id);
    if (!unread.length) return;
    setMarkingAll(true);
    try {
      await markNotificationsRead(unread);
    } catch (error) {
      console.warn("Failed to mark all read:", error?.message);
      RedNotify("Could not mark all read.");
    } finally {
      setMarkingAll(false);
    }
  }, [items]);

  const handleRowClick = useCallback(
    (item) => {
      if (!item.isRead) markAsRead(item.id);
      const route = notificationDeepLink(item.details);
      if (route) navigate(route);
    },
    [markAsRead, navigate]
  );

  const confirmDelete = useCallback((item) => {
    Modal.confirm({
      title: "Delete notification",
      content:
        "This notification will be permanently removed. This action cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          await deleteNotification(item.id);
          GreenNotify("Notification deleted");
        } catch (error) {
          console.warn("Failed to delete notification:", error?.message);
          RedNotify("Could not delete notification.");
        }
      },
    });
  }, []);

  const clearAll = useCallback(() => {
    if (!adminUid || items.length === 0) return;
    Modal.confirm({
      title: "Clear all notifications",
      content:
        "Every notification in your inbox will be permanently removed. This action cannot be undone.",
      okText: "Clear all",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        setClearingAll(true);
        try {
          await deleteAllNotifications(adminUid);
          GreenNotify("All notifications cleared");
        } catch (error) {
          console.warn("Failed to clear notifications:", error?.message);
          RedNotify("Could not clear notifications.");
        } finally {
          setClearingAll(false);
        }
      },
    });
  }, [adminUid, items.length]);

  return (
    <div className="notif-page">
      <div className="notif-header">
        <div className="notif-header-titles">
          <span className="notif-eyebrow">Inbox</span>
          <h2 className="notif-title">
            Notifications
            {unreadCount > 0 && (
              <Badge
                className="notif-unread-badge"
                count={unreadCount}
                overflowCount={99}
                color="#c2603d"
              />
            )}
          </h2>
          <p className="notif-subtitle">
            {unreadCount > 0
              ? `${unreadCount} unread ${
                  unreadCount === 1 ? "message" : "messages"
                }`
              : "You're all caught up"}
          </p>
        </div>
        <div className="notif-header-actions">
          <Button
            icon={<CheckOutlined />}
            onClick={markAllRead}
            loading={markingAll}
            disabled={unreadCount === 0}
          >
            Mark all read
          </Button>
          <Button
            danger
            icon={<ClearOutlined />}
            onClick={clearAll}
            loading={clearingAll}
            disabled={items.length === 0}
          >
            Clear all
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="notif-state">
          <Spin size="large" />
        </div>
      ) : items.length === 0 ? (
        <div className="notif-state">
          <Empty description="No notifications yet" />
        </div>
      ) : (
        <ul className="notif-list">
          {items.map((item) => (
            <li
              key={item.id}
              className={`notif-card${item.isRead ? "" : " is-unread"}`}
              onClick={() => handleRowClick(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleRowClick(item);
              }}
            >
              <span className="notif-indicator" aria-hidden>
                {!item.isRead && <span className="notif-dot" />}
              </span>
              <div className="notif-body">
                <div className="notif-card-top">
                  <span className="notif-card-title">
                    {item.title || "Notification"}
                  </span>
                  {!item.isRead && (
                    <Tag color="#c2603d" className="notif-new-tag">
                      New
                    </Tag>
                  )}
                </div>
                {item.body && <p className="notif-text">{item.body}</p>}
                {/* `details` is the deep-link payload (an OBJECT: {type, ids…}),
                    not display text — only render it if it's actually a string,
                    else React throws "objects are not valid as a child" (#31). */}
                {typeof item.details === "string" && item.details && (
                  <p className="notif-details">{item.details}</p>
                )}
                <div className="notif-meta">
                  <Tooltip title={relativeLabel(item.sentAt)}>
                    <span className="notif-time">
                      {formatTimestamp(item.sentAt)}
                    </span>
                  </Tooltip>
                  {item.status && (
                    <span className="notif-status">{item.status}</span>
                  )}
                </div>
              </div>
              <div
                className="notif-actions"
                onClick={(e) => e.stopPropagation()}
              >
                {!item.isRead && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckOutlined />}
                    loading={busyId === item.id}
                    onClick={() => markAsRead(item.id)}
                  >
                    Mark read
                  </Button>
                )}
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => confirmDelete(item)}
                >
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Notifications;
